import React from "react";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import clsx from "clsx";
import ProductComponentInfo from "features/ProductSearch/ProductComponentInfo";
import { observer } from "mobx-react-lite";
import {
  ComponentDetailsByFileOut,
  InternalProductSearchOutput,
} from "api-client";
import ProductDetailsModal from "features/ProductDetailsModal";
import { COEStore } from "../ComponentEditorStore";

interface ComponentChildContentProps {
  childItem: ComponentDetailsByFileOut;
  removable?: boolean;
  hideInfoIcon?: boolean;
  className?: string;
  reorderOptions?: React.ReactElement;
  moveUp?: () => void;
  moveDown?: () => void;
  order?: number;
  showOrder?: boolean;
}

const ComponentChildContent = ({
  childItem,
  removable,
  hideInfoIcon,
  className,
  ...props
}: ComponentChildContentProps) => {
  const [openDetails, setOpenDetails] = React.useState(false);

  const removeRow = (child: ComponentDetailsByFileOut) => {
    COEStore.onChangeData(
      "childrenList",
      COEStore.data.childrenList.filter((item) => child != item)
    );
    COEStore.getComponentCompliance();
  };

  function showDetailsIcon() {
    return (
      <IconInfoCircle
        className={clsx(
          "text-indigo-700 hover:text-indigo-500 w-6 h-6 cursor-pointer"
        )}
        onClick={() => setOpenDetails(true)}
      />
    );
  }

  function hoverActions() {
    return (
      <div
        className={clsx(
          "hidden absolute right-0 h-full top-0 transition",
          "group-hover/row:flex justify-end"
        )}
      >
        <div className="w-10 bg-gradient-to-r from-transparent to-indigo-50"></div>
        <div className="flex items-center bg-indigo-50 pr-4">
          {props.reorderOptions}
          {showDetailsIcon()}
          {removable ? (
            <IconTrash
              className="text-red-700 hover:text-red-500 cursor-pointer w-6 h-6 ml-1.5 mr-0"
              onClick={() => removeRow(childItem)}
            />
          ) : null}
        </div>
      </div>
    );
  }

  /**
   * Workaround to handle different shapes of compliance. Somehow somewhere we are feeding the wrong shape.
   */
  const childWithCompliances = {
    ...childItem,
    compliant_conformities_names: childItem.compliance?.conformities,
    compliant_certifications_names: childItem.compliance?.certifications,
    din_category: childItem.din_category_level_1,
    din_category_number: childItem.din_category_level_1_number,
  } as InternalProductSearchOutput;

  if (
    "compliant_conformities_names" in childItem &&
    childItem["compliant_conformities_names"]
  ) {
    childWithCompliances["compliant_conformities_names"] = childItem[
      "compliant_conformities_names"
    ] as string[];
  }

  if (
    "compliant_certifications_names" in childItem &&
    childItem["compliant_certifications_names"]
  ) {
    childWithCompliances["compliant_certifications_names"] = childItem[
      "compliant_certifications_names"
    ] as string[];
  }

  if ("din_category" in childItem && childItem["din_category"]) {
    childWithCompliances["din_category"] = childItem["din_category"] as string;
  }
  if ("din_category_number" in childItem && childItem["din_category_number"]) {
    childWithCompliances["din_category_number"] = childItem[
      "din_category_number"
    ] as string;
  }

  return (
    <div
      className={clsx(
        "w-full group/row",
        {
          "bg-gradient-to-r from-teal-50 to-white hover:!to-indigo-50":
            childItem.type === "PRODUCT",
          "bg-gradient-to-r from-cyan-50 to-white hover:!to-indigo-50":
            childItem.type === "COMPONENT",
        },
        className
      )}
    >
      <ProductComponentInfo
        item={childWithCompliances}
        detailsIcon={hideInfoIcon ? null : showDetailsIcon()}
        iconClassName={clsx("group-hover/row:hidden")}
        orderClassName={clsx("group-hover/row:block")}
        order={props.order}
        showOrder={props.showOrder}
      />
      {removable && hoverActions()}
      <ProductDetailsModal
        productId={childItem.id}
        productName={childItem.name}
        productType={childItem.type}
        open={openDetails}
        close={() => setOpenDetails(false)}
      />
    </div>
  );
};

export default observer(ComponentChildContent);
